import { BrowserRouter, Routes, Route, Link, useNavigate, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { SettingsProvider, useSiteSettings } from "@/contexts/SettingsContext";
import { Toaster } from "@/components/ui/sonner";
import Footer from "@/components/Footer";
import SiteHead from "@/components/SiteHead";
import PromoBanner from "@/components/PromoBanner";
import PrelaunchBanner from "@/components/PrelaunchBanner";
import ScrollToTop from "@/components/ScrollToTop";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuTrigger, DropdownMenuSeparator
} from "@/components/ui/dropdown-menu";
import { Trees, User, Menu, Plus, LayoutDashboard, MapPin, CalendarDays, LogOut, MessageCircle, Settings, Award, Inbox, Shield, ShieldCheck, Heart } from "lucide-react";
import { useState, useEffect } from "react";
import api from "@/lib/api";
import NotificationBell from "@/components/NotificationBell";
import "@/App.css";

import HomePage from "@/pages/HomePage";
import LoginPage from "@/pages/LoginPage";
import RegisterPage from "@/pages/RegisterPage";
import SpacesListPage from "@/pages/SpacesListPage";
import SpaceDetailPage from "@/pages/SpaceDetailPage";
import CreateSpacePage from "@/pages/CreateSpacePage";
import MySpacesPage from "@/pages/MySpacesPage";
import MyBookingsPage from "@/pages/MyBookingsPage";
import DashboardPage from "@/pages/DashboardPage";
import PaymentSuccessPage from "@/pages/PaymentSuccessPage";
import MessagesPage from "@/pages/MessagesPage";
import ManageCalendarPage from "@/pages/ManageCalendarPage";
import SettingsPage from "@/pages/SettingsPage";
import ProfilePage from "@/pages/ProfilePage";
import HostOnboardingPage from "@/pages/HostOnboardingPage";
import ForgotPasswordPage from "@/pages/ForgotPasswordPage";
import ResetPasswordPage from "@/pages/ResetPasswordPage";
import SuperstarProgramPage from "@/pages/SuperstarProgramPage";
import RequestsPage from "@/pages/RequestsPage";
import UserProfilePage from "@/pages/UserProfilePage";
import SubscriptionPage from "@/pages/SubscriptionPage";
import AdminPanelPage from "@/pages/AdminPanelPage";
import AdminVerificationsPage from "@/pages/AdminVerificationsPage";
import ClaimAccountPage from "@/pages/ClaimAccountPage";
import LegalPage from "@/pages/LegalPage";
import FavoritesPage from "@/pages/FavoritesPage";
import AboutPage from "@/pages/AboutPage";

function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="flex h-[80vh] items-center justify-center"><div className="animate-spin h-8 w-8 border-4 border-zinc-300 border-t-emerald-700 rounded-full" /></div>;
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

/**
 * If pre-launch mode is enabled in settings, show the splash page to everyone
 * except admins. Admins, login/register, reset, waitlist, and admin routes still work.
 */
function PrelaunchGate({ children }) {
  // The pre-launch flag now keeps the entire site accessible. Booking endpoints
  // are blocked at the API layer (POST /api/bookings) and the booking CTA on
  // SpaceDetailPage is disabled. Everyone can browse, register and publish.
  return children;
}

function Header() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const { settings } = useSiteSettings();
  const [unread, setUnread] = useState(0);
  const appName = settings?.app_name || "";
  const logoImage = settings?.logo_image || "";
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
  const handleLogout = async () => { await logout(); navigate("/"); };

  const logoUrl = logoImage ? (logoImage.startsWith("/api/") ? `${BACKEND_URL}${logoImage}` : logoImage) : "";

  useEffect(() => {
    if (!user) return;
    const load = () => api.get("/messages/unread-count").then(r => setUnread(r.data.count)).catch(() => {});
    load();
    const interval = setInterval(load, 15000);
    return () => clearInterval(interval);
  }, [user]);

  return (
    <header className="sticky top-0 z-50 backdrop-blur-xl bg-white/80 border-b border-zinc-200/50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2 font-heading font-bold text-xl text-zinc-950" data-testid="logo-link">
          {logoUrl ? (
            <>
              <img src={logoUrl} alt={appName} className="h-9 w-auto max-w-[60px] object-contain" />
              <span>{appName}</span>
            </>
          ) : (
            <><Trees className="h-6 w-6 text-emerald-600" />{appName}</>
          )}
        </Link>
        <nav className="hidden md:flex items-center gap-6">
          {(() => {
            const order = Array.isArray(settings?.nav_order) && settings.nav_order.length
              ? settings.nav_order
              : ["explore", "create", "superstar", "about"];
            const ITEMS = {
              explore: <Link key="explore" to="/spaces" className="text-sm font-medium text-zinc-600 hover:text-zinc-950 transition-colors" data-testid="nav-search-link">Explorer</Link>,
              create: <Link key="create" to="/create-space" className="text-sm font-medium text-zinc-600 hover:text-zinc-950 transition-colors" data-testid="nav-create-link">Publier un terrain</Link>,
              superstar: (
                <Link key="superstar" to="/superstar" className="text-sm font-medium text-amber-600 hover:text-amber-700 transition-colors flex items-center gap-1" data-testid="nav-superstar-link">
                  <Award className="h-3.5 w-3.5" /> Superstar
                </Link>
              ),
              about: <Link key="about" to="/about" className="text-sm font-medium text-zinc-600 hover:text-zinc-950 transition-colors" data-testid="nav-about-link">À propos</Link>,
            };
            return order.map((k) => ITEMS[k]).filter(Boolean);
          })()}
        </nav>
        <div className="flex items-center gap-3">
          {user ? (
            <>
            <NotificationBell />
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="flex items-center gap-2" data-testid="user-menu-trigger">
                  <div className="h-8 w-8 rounded-full bg-emerald-700 flex items-center justify-center"><User className="h-4 w-4 text-white" /></div>
                  <span className="hidden sm:inline text-sm font-medium">{user.name}</span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48">
                <DropdownMenuItem onClick={() => navigate("/profile")} data-testid="menu-profile">
                  <User className="mr-2 h-4 w-4" /> Mon profil
                </DropdownMenuItem>
                {(user.spaces_count ?? 0) > 0 && (
                  <DropdownMenuItem onClick={() => navigate("/dashboard")} data-testid="menu-dashboard"><LayoutDashboard className="mr-2 h-4 w-4" /> Tableau de bord</DropdownMenuItem>
                )}
                <DropdownMenuItem onClick={() => navigate("/my-spaces")} data-testid="menu-my-spaces"><MapPin className="mr-2 h-4 w-4" /> Mes terrains</DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate("/my-bookings")} data-testid="menu-my-bookings"><CalendarDays className="mr-2 h-4 w-4" /> Mes réservations</DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate("/favorites")} data-testid="menu-favorites"><Heart className="mr-2 h-4 w-4" /> Mes favoris</DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate("/messages")} data-testid="menu-messages">
                  <MessageCircle className="mr-2 h-4 w-4" /> Messages {unread > 0 && <Badge className="ml-auto bg-emerald-600 text-white border-0 text-xs">{unread}</Badge>}
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate("/create-space")} data-testid="menu-create-space" className="md:hidden"><Plus className="mr-2 h-4 w-4" /> Publier un terrain</DropdownMenuItem>
                <DropdownMenuSeparator />
                {user.role === "admin" && (
                  <>
                  <DropdownMenuItem onClick={() => navigate("/admin")} data-testid="menu-admin-panel">
                    <Shield className="mr-2 h-4 w-4" /> Panneau admin
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate("/admin/verifications")} data-testid="menu-admin-vérifications">
                    <ShieldCheck className="mr-2 h-4 w-4" /> Verifications d'identité
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate("/settings")} data-testid="menu-settings">
                    <Settings className="mr-2 h-4 w-4" /> Parametres du site
                  </DropdownMenuItem>
                  </>
                )}
                <DropdownMenuItem onClick={handleLogout} data-testid="menu-logout"><LogOut className="mr-2 h-4 w-4" /> Deconnexion</DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            </>
          ) : (
            <div className="flex items-center gap-2">
              <Link to="/login" data-testid="nav-login-link"><Button variant="ghost" size="sm">Connexion</Button></Link>
              <Link to="/register" data-testid="nav-register-link"><Button size="sm" className="bg-emerald-700 text-white hover:bg-emerald-800">Inscription</Button></Link>
            </div>
          )}
          <div className="md:hidden">
            <DropdownMenu>
              <DropdownMenuTrigger asChild><Button variant="ghost" size="icon" data-testid="mobile-menu-trigger"><Menu className="h-5 w-5" /></Button></DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => navigate("/spaces")}>Explorer</DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate("/create-space")}>Publier</DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate("/superstar")}>Superstar</DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate("/about")}>À propos</DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </div>
    </header>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
      <SettingsProvider>
        <ScrollToTop />
        <SiteHead />
        <PrelaunchGate>
        <div className="min-h-screen bg-[#FAFAFA]">
          <PromoBanner />
          <PrelaunchBanner />
          <Header />
          <Routes>
            <Route path="/" element={<HomePage />} />
            <Route path="/login" element={<LoginPage />} />
            <Route path="/register" element={<RegisterPage />} />
            <Route path="/forgot-password" element={<ForgotPasswordPage />} />
            <Route path="/reset-password" element={<ResetPasswordPage />} />
            <Route path="/claim" element={<ClaimAccountPage />} />
            <Route path="/cgu" element={<LegalPage which="cgu" />} />
            <Route path="/confidentialite" element={<LegalPage which="privacy" />} />
            <Route path="/annulation" element={<LegalPage which="cancellation" />} />
            <Route path="/about" element={<AboutPage />} />
            <Route path="/superstar" element={<SuperstarProgramPage />} />
            <Route path="/requests" element={<ProtectedRoute><RequestsPage /></ProtectedRoute>} />
            <Route path="/users/:userId" element={<UserProfilePage />} />
            <Route path="/subscription" element={<ProtectedRoute><SubscriptionPage /></ProtectedRoute>} />
            <Route path="/admin" element={<ProtectedRoute><AdminPanelPage /></ProtectedRoute>} />
            <Route path="/admin/verifications" element={<ProtectedRoute><AdminVerificationsPage /></ProtectedRoute>} />
            <Route path="/spaces" element={<SpacesListPage />} />
            <Route path="/spaces/:id" element={<SpaceDetailPage />} />
            <Route path="/create-space" element={<ProtectedRoute><CreateSpacePage /></ProtectedRoute>} />
            <Route path="/edit-space/:spaceId" element={<ProtectedRoute><CreateSpacePage /></ProtectedRoute>} />
            <Route path="/my-spaces" element={<ProtectedRoute><MySpacesPage /></ProtectedRoute>} />
            <Route path="/my-bookings" element={<ProtectedRoute><MyBookingsPage /></ProtectedRoute>} />
            <Route path="/favorites" element={<ProtectedRoute><FavoritesPage /></ProtectedRoute>} />
            <Route path="/dashboard" element={<ProtectedRoute><DashboardPage /></ProtectedRoute>} />
            <Route path="/payment/success" element={<ProtectedRoute><PaymentSuccessPage /></ProtectedRoute>} />
            <Route path="/messages" element={<ProtectedRoute><MessagesPage /></ProtectedRoute>} />
            <Route path="/manage-calendar/:id" element={<ProtectedRoute><ManageCalendarPage /></ProtectedRoute>} />
            <Route path="/settings" element={<ProtectedRoute><SettingsPage /></ProtectedRoute>} />
            <Route path="/profile" element={<ProtectedRoute><ProfilePage /></ProtectedRoute>} />
            <Route path="/host-onboarding" element={<ProtectedRoute><HostOnboardingPage /></ProtectedRoute>} />
          </Routes>
        </div>
        <Footer />
        </PrelaunchGate>
        <Toaster richColors position="top-right" closeButton duration={3500} />
      </SettingsProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
